import R from 'ramda';

import { ItemAttrs, OptionBindingAttrs, OptionAttrs, OptionValueAttrs, Option } from './types';

export const pickItemAttrs = <T extends ItemAttrs>(item: T): ItemAttrs => {
  return R.clone({
    additives: item.additives,
    alcohol_percentage: item.alcohol_percentage,
    allergens: item.allergens,
    availability_period: item.availability_period,
    baseprice: item.baseprice,
    barcode_gtin: item.barcode_gtin || null,
    brand_id: item.brand_id || null,
    caffeine_content: item.caffeine_content,
    conditions_of_use_and_storage: item.conditions_of_use_and_storage,
    country_of_origin: item.country_of_origin,
    courier_restrictions: item.courier_restrictions,
    delivery_methods: item.delivery_methods,
    deposit_permutation_id: item.deposit_permutation_id,
    description: item.description,
    dietary_preferences: item.dietary_preferences,
    discounted: item.discounted,
    display_unit: item.display_unit,
    source_config: item.source_config,
    distributor_information: item.distributor_information,
    enabled: item.enabled,
    external_id: item.external_id,
    forbidden_courier_capabilities: item.forbidden_courier_capabilities,
    image: item.image,
    images: item.images,
    ingredients: item.ingredients,
    inventory_mode: item.inventory_mode,
    is_bundle_offer: item.is_bundle_offer,
    mandatory_warnings: item.mandatory_warnings,
    menu_id: item.menu_id,
    merchant_sku: item.merchant_sku || null,
    more_information: item.more_information,
    name: item.name,
    number_of_units: item.number_of_units,
    nutrition_facts: item.nutrition_facts,
    nutrition_values: item.nutrition_values,
    nutrition_values_baseline: item.nutrition_values_baseline,
    producer_information: item.producer_information,
    product_hierarchy_level_id: item.product_hierarchy_level_id,
    required_courier_capabilities: item.required_courier_capabilities,
    restrictions: item.restrictions,
    user_requirements: item.user_requirements,
    vat_percentage: item.vat_percentage,
    vat_category_code: item.vat_category_code,
    volume_in_ml: item.volume_in_ml,
    weekly_availability: item.weekly_availability,
    weekly_visibility: item.weekly_visibility,
    weight_in_grams: item.weight_in_grams,
    max_quantity_per_purchase: item.max_quantity_per_purchase,
    min_quantity_per_purchase: item.min_quantity_per_purchase,
    sell_by_weight_config: item.sell_by_weight_config,
    external_info_hyperlink: item.external_info_hyperlink,
    wolt_plus_only: item.wolt_plus_only,
    return_policy: item.return_policy,
  });
};

export const pickOptionValueAttrs = <T extends OptionValueAttrs>(optionValue: T): OptionValueAttrs => {
  return R.clone({
    additives: optionValue.additives,
    allergens: optionValue.allergens,
    availability_period: optionValue.availability_period,
    deposit_permutation_id: optionValue.deposit_permutation_id,
    dietary_preferences: optionValue.dietary_preferences,
    distributor_information: optionValue.distributor_information,
    enabled: optionValue.enabled,
    external_id: optionValue.external_id,
    ingredients: optionValue.ingredients,
    mandatory_warnings: optionValue.mandatory_warnings,
    multi_choice_config: optionValue.multi_choice_config,
    name: optionValue.name,
    nutrition_facts: optionValue.nutrition_facts,
    price: optionValue.price,
    producer_information: optionValue.producer_information,
    vat_percentage: optionValue.vat_percentage,
    vat_category_code: optionValue.vat_category_code,
    visible_to_courier: optionValue.visible_to_courier,
    visible_to_merchant_app: optionValue.visible_to_merchant_app,
  });
};

export const pickOptionAttrs = <T extends OptionAttrs>(option: T): OptionAttrs => {
  return R.clone({
    menu_id: option.menu_id,
    type: option.type,
    name: option.name,
    external_id: option.external_id,
  });
};

export const pickOptionBindingAttrs = <T extends OptionBindingAttrs>(optionBinding: T): OptionBindingAttrs => {
  return R.clone({
    option_id: optionBinding.option_id,
    prerequisite_values: optionBinding.prerequisite_values,
    multi_choice_config: optionBinding.multi_choice_config,
    name: optionBinding.name,
    external_id: optionBinding.external_id,
  });
};

export const getDefaultValueIndex = (option: Option): number => {
  const defaultValueIndex = option.values.findIndex(optionValue => optionValue.id === option.default_value);
  if (defaultValueIndex < 0) {
    throw new Error("Option's `default_value` is referring to a non-existing option value");
  }
  return defaultValueIndex;
};
